import { SpanStatusCode, context, trace } from '@opentelemetry/api';

import { getTracer } from './otelProvider';
import { formatTraceparent } from './traceUtils';

/**
 * Singleton manager for trace context.
 *
 * This allows accessing the current trace context from outside React components
 * (e.g., Apollo Links, utility functions).
 *
 * The TraceProvider React component should sync with this manager.
 */
class TraceContextManager {
  private currentTraceparent: Nullable<string> = null;
  private tracer = getTracer();

  getCurrentTraceparent(): Nullable<string> {
    return this.currentTraceparent;
  }

  setCurrentTraceparent(traceparent: Nullable<string>): void {
    this.currentTraceparent = traceparent;
  }

  /**
   * Execute a function within a traced context with a named span.
   * This creates an actual OTel span for distributed tracing.
   *
   * @param spanName - Name of the span (e.g., 'select-energy-tariff', 'submit-application')
   * @param fn - Function to execute within the span
   * @param attributes - Optional span attributes for additional context
   */
  async withTrace<T>(
    spanName: string,
    fn: () => T | Promise<T>,
    attributes?: Record<string, string | number | boolean>
  ): Promise<T> {
    // Create a new span for this operation
    const span = this.tracer.startSpan(spanName, {
      attributes,
    });

    // Generate traceparent for this span
    const spanContext = span.spanContext();
    const traceparent = formatTraceparent(
      spanContext.traceId,
      spanContext.spanId,
      true
    );
    this.setCurrentTraceparent(traceparent);

    // Execute function within span context
    return context.with(trace.setSpan(context.active(), span), async () => {
      try {
        const result = await Promise.resolve(fn());
        span.setStatus({ code: SpanStatusCode.OK });
        return result;
      } catch (error) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : String(error),
        });
        span.recordException(error as Error);
        throw error;
      } finally {
        span.end();
        this.setCurrentTraceparent(null);
      }
    });
  }
}

export const traceContextManager = new TraceContextManager();
